import React, { useEffect, useId, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { AnimatePresence, motion } from 'framer-motion';
import { LuCornerDownLeft, LuSearch } from 'react-icons/lu';
import { GLASS_CHIP, GLASS_POPOVER } from '../ui/glass';
import { getMenuIcon } from './menuConfig';
import useMenuSearch from './useMenuSearch';
import { useTranslation } from '../../i18n/LanguageContext';

/**
 * Menu search: type to find a page, ↑/↓ to move, Enter to open, Esc to close.
 * `withShortcut` also focuses it on Ctrl/⌘+K (only one instance should).
 */
const TopBarSearch = ({ autoFocus = false, withShortcut = false, onDone, className = '' }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const listId = useId();
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const results = useMenuSearch(term);
  const showPanel = open && term.trim().length > 0;

  useEffect(() => {
    if (autoFocus) inputRef.current?.focus();
  }, [autoFocus]);

  useEffect(() => {
    setActiveIndex(0);
  }, [term]);

  // Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!withShortcut) return undefined;
    const handleShortcut = (event) => {
      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        inputRef.current?.focus();
        setOpen(true);
      }
    };
    document.addEventListener('keydown', handleShortcut);
    return () => document.removeEventListener('keydown', handleShortcut);
  }, [withShortcut]);

  const openResult = (path) => {
    navigate(path);
    setTerm('');
    setOpen(false);
    if (onDone) onDone();
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Escape') {
      setOpen(false);
      setTerm('');
      inputRef.current?.blur();
      if (onDone) onDone();
    } else if (event.key === 'ArrowDown' && results.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => (index + 1) % results.length);
    } else if (event.key === 'ArrowUp' && results.length > 0) {
      event.preventDefault();
      setActiveIndex((index) => (index - 1 + results.length) % results.length);
    } else if (event.key === 'Enter' && results[activeIndex]) {
      event.preventDefault();
      openResult(results[activeIndex].path);
    }
  };

  return (
    <div ref={containerRef} className={`tw-relative ${className}`}>
      <div className={`tw-flex tw-h-10 tw-items-center tw-gap-2.5 tw-rounded-full tw-px-3.5 tw-transition-shadow focus-within:tw-ring-2 focus-within:tw-ring-teal-500/40 ${GLASS_CHIP}`}>
        <LuSearch aria-hidden="true" className="tw-h-4 tw-w-4 tw-shrink-0 tw-text-slate-400" />
        <input
          ref={inputRef}
          type="search"
          value={term}
          onChange={(event) => {
            setTerm(event.target.value);
            setOpen(true);
          }}
          onFocus={() => setOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder={t('topbar.search.placeholder')}
          role="combobox"
          aria-expanded={showPanel}
          aria-controls={listId}
          aria-autocomplete="list"
          aria-activedescendant={showPanel && results[activeIndex] ? `${listId}-${activeIndex}` : undefined}
          className="tw-h-full tw-min-w-0 tw-flex-1 tw-border-0 tw-bg-transparent tw-text-base tw-text-slate-800 sm:tw-text-sm tw-shadow-none tw-outline-none placeholder:tw-text-slate-400 dark:tw-text-slate-100 dark:placeholder:tw-text-slate-500"
        />
        {withShortcut && (
          <kbd className="tw-hidden tw-rounded-md tw-border tw-border-slate-200 tw-bg-white/70 tw-px-1.5 tw-py-0.5 tw-font-sans tw-text-[10px] tw-font-semibold tw-text-slate-400 lg:tw-block dark:tw-border-white/10 dark:tw-bg-white/5 dark:tw-text-slate-500">
            Ctrl K
          </kbd>
        )}
      </div>

      <AnimatePresence>
        {showPanel && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="tw-absolute tw-inset-x-0 tw-top-[calc(100%+0.5rem)] tw-z-10"
          >
            <div className={`tw-max-h-80 tw-overflow-y-auto tw-rounded-2xl tw-p-1.5 glass-scrollbar ${GLASS_POPOVER}`}>
              {results.length === 0 ? (
                <p className="tw-px-3 tw-py-6 tw-text-center tw-text-sm tw-text-slate-500 dark:tw-text-slate-400">{t('topbar.search.noResults')}</p>
              ) : (
                <ul id={listId} role="listbox" className="tw-space-y-0.5">
                  {results.map((result, index) => {
                    const Icon = getMenuIcon(result.groupPath);
                    const isActive = index === activeIndex;
                    return (
                      <li
                        key={result.path}
                        id={`${listId}-${index}`}
                        role="option"
                        aria-selected={isActive}
                        onMouseEnter={() => setActiveIndex(index)}
                        onMouseDown={(event) => event.preventDefault()}
                        onClick={() => openResult(result.path)}
                        className={`tw-flex tw-cursor-pointer tw-items-center tw-gap-3 tw-rounded-xl tw-px-2.5 tw-py-2 ${isActive ? 'tw-bg-teal-500/10 dark:tw-bg-teal-400/10' : 'tw-bg-transparent'}`}
                      >
                        <span className={`tw-grid tw-h-8 tw-w-8 tw-shrink-0 tw-place-items-center tw-rounded-lg tw-text-slate-500 dark:tw-text-slate-300 ${GLASS_CHIP}`}>
                          <Icon aria-hidden="true" className="tw-h-4 tw-w-4" />
                        </span>
                        <span className="tw-min-w-0 tw-flex-1">
                          <span className="tw-block tw-truncate tw-text-sm tw-font-medium tw-text-slate-800 dark:tw-text-slate-100">{result.title}</span>
                          {result.group && (
                            <span className="tw-block tw-truncate tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">{result.group}</span>
                          )}
                        </span>
                        {isActive && <LuCornerDownLeft aria-hidden="true" className="tw-h-3.5 tw-w-3.5 tw-shrink-0 tw-text-slate-400" />}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

TopBarSearch.propTypes = {
  autoFocus: PropTypes.bool,
  withShortcut: PropTypes.bool,
  onDone: PropTypes.func,
  className: PropTypes.string,
};

export default TopBarSearch;
